import { Component, CUSTOM_ELEMENTS_SCHEMA, inject, signal } from '@angular/core';
import { AddBriefDemographicsDto, BriefResponseDto, CompleteDemographicsDto, CreateBriefDemographicDto, InfluencerLevelByChannel, InfluencerTypeEnum, SocialChannelEnum } from '../../common/general-types/briefs.dto';
import { ActivatedRoute } from '@angular/router';
import { BriefService } from '../../services/brief/brief.service';
import { Location } from '@angular/common';
import { PricingModelV2Service } from '../../services/pricing-model-v2/pricing-model-v2.service';
import { environment } from '../../../environments/environment';
import { CurrencyPipe, DecimalPipe } from '@angular/common';
import { InfluencerLevel } from '../../common/general-types/pricing.dto';
import { FormatService } from '../../common/format/format.service';

export type InfluencerChannel = 'tiktok' | 'instagram' | 'youtube';

@Component({
  selector: 'app-brief-auto-hire-config',
  standalone: true,
  imports: [CurrencyPipe, DecimalPipe],
  templateUrl: './brief-auto-hire-config.component.html',
  styleUrl: './brief-auto-hire-config.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class BriefAutoHireConfigComponent {

  showHelp = signal<boolean>(true);

  briefId = signal<string | undefined>(undefined);
  brief = signal<BriefResponseDto | undefined>(undefined);

  /** Auto-Config Destination */
  selectedDemographics = signal<AddBriefDemographicsDto | undefined>(undefined);
  /** Auto-Config Destination - END */

  /** Budget Variables */
  budgetValue = signal<number>(1000);
  estimatedContentItems = signal<number>(0);
  /** Budget Variables - END */

  /** Influencer tier columns: TikTok, Instagram, YouTube */
  readonly channels: { id: InfluencerChannel; label: string }[] = [
    { id: 'tiktok', label: 'TikTok' },
    { id: 'instagram', label: 'Instagram' },
    { id: 'youtube', label: 'YouTube' },
  ];

  /** Quick presets: creators count and budget (e.g. 5 creators @ £5,000). */
  readonly quickPresets: { creators: number; budget: number }[] = [
    { creators: 5, budget: 500 },
    { creators: 5, budget: 1000 },
    { creators: 5, budget: 5000 },
    { creators: 10, budget: 10000 },
    { creators: 15, budget: 15000 },
  ];

  /** Services */
  private location = inject(Location);
  private route = inject(ActivatedRoute);
  public briefService = inject(BriefService);
  private pricingModelService = inject(PricingModelV2Service);
  formatService = inject(FormatService);
  /** Services - END */


  ngOnInit(): void {
    this.briefId.set(this.route.snapshot.params['briefId']);
    if (this.briefId()) {
      this.getBrief();
    }
  }

  getBrief() {
    this.briefService.getBriefById(this.briefId()!).subscribe((brief: BriefResponseDto) => {
      console.log('brief: ', brief);
      this.brief.set(brief);

      if (brief.demographics) {
        this.populateScreenConfig(brief.demographics as CompleteDemographicsDto);
      }
    }, error => {
      console.error('error getting brief: ', error);
    });
  }

  populateScreenConfig(demographics: CompleteDemographicsDto) {

    this.selectedDemographics.set({
      gender: this.setGenderSelections(demographics.gender),
      interests: this.setInterestSelections(demographics.interests),
      location: demographics.location,
      age: demographics.age,
      categoryId: demographics.categoryId,
      subCategoryId: demographics.subCategoryId,
      countryCodes: demographics.countryCodes,
      is18plus: demographics.is18plus,
      influencerType: demographics.influencerType,
      followerRanges: demographics.followerRanges,
    });

    if (demographics.followerRanges && demographics.followerRanges.length > 0) {
      if (this.brief()!.shoutBrief!.socialPlatform == SocialChannelEnum['TikTok Post']) {
       this.selectedDemographics.set({ ...this.selectedDemographics()!, influencerLevelByChannel: { tiktok: { followersFrom: demographics.followerRanges[0].min, followersTo: demographics.followerRanges[0].max } } });
      } else if (this.brief()!.shoutBrief!.socialPlatform == SocialChannelEnum['Instagram Reel'] || this.brief()!.shoutBrief!.socialPlatform == SocialChannelEnum['Instagram Story']) {
        this.selectedDemographics.set({ ...this.selectedDemographics()!, influencerLevelByChannel: { instagram: { followersFrom: demographics.followerRanges[0].min, followersTo: demographics.followerRanges[0].max } } });
      } else if (this.brief()!.shoutBrief!.socialPlatform == SocialChannelEnum['YouTube Shorts']) {
        this.selectedDemographics.set({ ...this.selectedDemographics()!, influencerLevelByChannel: { youtube: { followersFrom: demographics.followerRanges[0].min, followersTo: demographics.followerRanges[0].max } } });
      }
    }
  }

  setGenderSelections(genders: number[]) {
    let genderSelections: number[] = [];
    let gendersForReturn: { id: number; name: string }[] = [];
    for (let g of genders) {
      genderSelections.push(Number(g));
      let found = this.briefService.creatorGenders.find(gender => gender.id === g);
      if (found) {
        gendersForReturn.push(found);
      }
    }
    return genderSelections;
  }

  setInterestSelections(interests: number[]) {
    let interestSelections: number[] = [];
    for (let i of interests) {
      interestSelections.push(Number(i));
    }
    return interestSelections;
  }

  /** Influencer levels from brief pricing (for dropdown options). */
  getInfluencerLevels(): InfluencerLevel[] {
    const levels = this.brief()?.currentPrice?.influencerLevels;
    return Array.isArray(levels) ? levels : [];
  }

  /** Build minimal demographics used when first setting a channel selection. */
  private getDefaultDemographics(): AddBriefDemographicsDto {
    return {
      gender: [],
      interests: [],
      location: [],
      age: [],
      categoryId: 0,
      subCategoryId: 0,
      countryCodes: [],
      is18plus: false,
      influencerType: [InfluencerTypeEnum.Any],
      followerRanges: [],
    };
  }

  getSelectedDemographics(): AddBriefDemographicsDto {
    return this.selectedDemographics() ?? this.getDefaultDemographics();
  }

  isAgeSelected(age: { id: number; label: string; minAge: number; maxAge: number }): boolean {
    const ages = this.selectedDemographics()?.age ?? [];
    return ages.some((a) => a.id === age.id);
  }

  onAgeToggle(age: { id: number; label: string; minAge: number; maxAge: number }): void {
    const current = this.selectedDemographics() ?? this.getDefaultDemographics();
    const ages = current.age ?? [];
    const isSelected = ages.some((a) => a.id === age.id);
    const updatedAges = isSelected
      ? ages.filter((a) => a.id !== age.id)
      : [...ages, age];
    this.selectedDemographics.set({ ...current, age: updatedAges });
  }

  isGenderSelected(gender: { id: number; name: string }): boolean {
    const genders = this.selectedDemographics()?.gender ?? [];
    return genders.includes(gender.id);
  }

  onGenderToggle(gender: { id: number; name: string }): void {
    console.log('onGenderToggle: ', gender);
    const current = this.selectedDemographics() ?? this.getDefaultDemographics();
    const genders = current.gender ?? [];
    const isSelected = genders.includes(gender.id);
    const updatedGenders = isSelected
      ? genders.filter((g) => g !== gender.id)
      : [...genders, gender.id];
    this.selectedDemographics.set({ ...current, gender: updatedGenders });
  }

  onInfluencerLevelChange(channel: InfluencerChannel, level: InfluencerLevel): void {
    const current = this.selectedDemographics() ?? this.getDefaultDemographics();
    const updated: AddBriefDemographicsDto = {
      ...current,
      influencerLevelByChannel: {
        ...current.influencerLevelByChannel,
        [channel]: { followersFrom: level.followersFrom, followersTo: level.followersTo },
      },
    };
    this.selectedDemographics.set(updated);
  }

  isCountrySelected(country: { countryName: string; countryCode: string }): boolean {
    const countries = this.selectedDemographics()?.countryCodes ?? [];
    return countries.includes(country.countryCode);
  }

  isInterestSelected(interest: { id: number | string; name: string }): boolean {
    const ids = this.selectedDemographics()?.interests ?? [];
    return ids.includes(Number(interest.id));
  }

  onInterestToggle(interest: { id: number | string; name: string }): void {
    const current = this.selectedDemographics() ?? this.getDefaultDemographics();
    const ids = current.interests ?? [];
    const id = Number(interest.id);
    const isSelected = ids.includes(id);
    const updated = isSelected ? ids.filter((i) => i !== id) : [...ids, id];
    this.selectedDemographics.set({ ...current, interests: updated });
  }

  onCountryToggle(country: { countryName: string; countryCode: string }): void {
    const current = this.selectedDemographics() ?? this.getDefaultDemographics();
    const countries = current.countryCodes ?? [];
    const isSelected = countries.includes(country.countryCode);
    const updatedCountries = isSelected
      ? countries.filter((c) => c !== country.countryCode)
      : [...countries, country.countryCode];
    this.selectedDemographics.set({ ...current, countryCodes: updatedCountries });
  }

  /** Called from template when dropdown selection changes (value is level index). */
  onInfluencerLevelIndexChange(channel: InfluencerChannel, indexStr: string): void {
    const levels = this.getInfluencerLevels();
    const index = parseInt(indexStr, 10);
    const level = levels[index];
    if (level) this.onInfluencerLevelChange(channel, level);
  }

  /** Index of the level that matches the selected range for this channel (for select value). */
  getSelectedLevelIndexForChannel(channel: InfluencerChannel): number {
    const range = this.selectedDemographics()?.influencerLevelByChannel?.[channel];
    if (!range) return 0;
    const levels = this.getInfluencerLevels();
    const idx = levels.findIndex(
      (l) => l.followersFrom === range.followersFrom && l.followersTo === range.followersTo
    );
    return idx >= 0 ? idx : 0;
  }

  /** Selected influencer level for this channel (for Bonus/Commission display). */
  getSelectedLevelForChannel(channel: InfluencerChannel): InfluencerLevel | undefined {
    const levels = this.getInfluencerLevels();
    if (!levels.length) return undefined;
    const idx = this.getSelectedLevelIndexForChannel(channel);
    return levels[idx];
  }

  /** Format follower range for dropdown option label. */
  formatLevelLabel(level: InfluencerLevel): string {
    const from = this.formatService.convertIntToString(level.followersFrom);
    const to = this.formatService.convertIntToString(level.followersTo);
    return `${level.title} (${from} - ${to})`;
  }

  getCurrencySymbol(): string {
    const code = this.brief()?.currentPrice?.currencyCode;
    if (!code) return '£';
    const map: Record<string, string> = {
      GBP: '£',
      USD: '$',
      EUR: '€',
      JPY: '¥',
      AUD: 'A$',
      CAD: 'C$',
      CHF: 'CHF',
      INR: '₹',
    };
    return map[String(code).toUpperCase()] ?? String(code).toUpperCase();
  }

  onBudgetInput(event: Event): void {
    const el = event.target as HTMLInputElement;
    const raw = el.value.replace(/[^0-9.]/g, '');
    const parsed = raw === '' ? 0 : Number(raw);
    this.budgetValue.set(Number.isNaN(parsed) ? 0 : Math.max(0, parsed));
  }

  onQuickPresetClick(creators: number, budget: number): void {
    this.budgetValue.set(budget);
    this.estimatedContentItems.set(creators);
  }

  isQuickPresetSelected(preset: { creators: number; budget: number }): boolean {
    return this.budgetValue() === preset.budget && this.estimatedContentItems() === preset.creators;
  }

  /** Apply VAT (20%) for GBP when agency is not in no-VAT list, same as lite-row-actions getTotalPriceV2. */
  private applyVatIfNeeded(price: number): number {
    const b = this.brief();
    if (!b?.currentPrice || !b?.businessAccount) return price;
    const noVatAgenciesIds: string[] = [environment.shoutAgencyId];
    if (
      b.currentPrice.currencyCode?.toUpperCase() === 'GBP' &&
      (!b.businessAccount.agencyId || !noVatAgenciesIds.includes(b.businessAccount.agencyId))
    ) {
      return price * 1.2;
    }
    return price;
  }

  getMinPricePerCreator(): number {
    const b = this.brief();
    if (!b) return 0;
    return this.applyVatIfNeeded(this.pricingModelService.getMinTotalPrice(b));
  }

  getMaxPricePerCreator(): number {
    const b = this.brief();
    if (!b) return 0;
    return this.applyVatIfNeeded(this.pricingModelService.getMaxTotalPrice(b));
  }

  getMinContentPieces(): number {
    const budget = this.budgetValue();
    const maxPrice = this.getMaxPricePerCreator();
    if (budget <= 0 || maxPrice <= 0) return 0;
    return Math.floor(budget / maxPrice);
  }

  getMaxContentPieces(): number {
    const budget = this.budgetValue();
    const minPrice = this.getMinPricePerCreator();
    if (budget <= 0 || minPrice <= 0) return 0;
    return Math.floor(budget / minPrice);
  }

  getMinRatePerVideo(): number {
    const b = this.brief();
    const videos = b?.videosPerCreator ?? 1;
    if (videos <= 0) return 0;
    return this.getMinPricePerCreator() / videos;
  }

  getMaxRatePerVideo(): number {
    const b = this.brief();
    const videos = b?.videosPerCreator ?? 1;
    if (videos <= 0) return 0;
    return this.getMaxPricePerCreator() / videos;
  }

  /** Price per creator when a single influencer level is selected for the brief's channel; otherwise null. */
  getPricePerCreatorForSelectedLevel(): number | null {
    const channel = this.getShoutBriefChannel();
    const level = channel != null ? this.getSelectedLevelForChannel(channel) : undefined;
    const b = this.brief();
    if (!b || !level) return null;
    const raw = this.pricingModelService.getTotalPriceForInfluencerLevel(b, level);
    return raw <= 0 ? null : this.applyVatIfNeeded(raw);
  }

  /** Exact content pieces when an influencer level is selected; otherwise null. */
  getExactContentPieces(): number | null {
    const price = this.getPricePerCreatorForSelectedLevel();
    if (price == null || price <= 0) return null;
    const budget = this.budgetValue();
    if (budget <= 0) return null;
    return Math.floor(budget / price);
  }

  /** Exact rate per video when an influencer level is selected; otherwise null. */
  getExactRatePerVideo(): number | null {
    const price = this.getPricePerCreatorForSelectedLevel();
    if (price == null || price <= 0) return null;
    const videos = this.brief()?.videosPerCreator ?? 1;
    if (videos <= 0) return null;
    return price / videos;
  }

  getRowIcon(channel: InfluencerChannel): string {
    switch (channel) {
      case 'tiktok':
        return '../../../../../assets/social-icon/tik-tok-transparent.svg';
      case 'instagram':
        return '../../../../../assets/social-icon/ig_reels_logo.png';
      case 'youtube':
        return '../../../../../assets/social-icon/youtube_shorts.webp';
    }
    return '../../../../../assets/social-icon/ugc-videos.svg';
  }

  /** Whether the brief has a shout brief with a social platform set (for showing single-platform influencer size V2). */
  hasInfluencerSizePlatform(): boolean {
    const sb = this.brief()?.shoutBrief;
    if (sb == null || sb.socialPlatform === undefined || sb.socialPlatform === null) return false;
    if (typeof sb.socialPlatform === 'number' && sb.socialPlatform === SocialChannelEnum['Not Set']) return false;
    return this.getShoutBriefChannel() != null;
  }

  /** Resolve InfluencerChannel from shoutBrief.socialPlatform for the horizontal size selector. */
  getShoutBriefChannel(): InfluencerChannel | null {
    const platform = this.brief()?.shoutBrief?.socialPlatform;
    if (platform === undefined || platform === null) return null;
    switch (Number(platform)) {
      case SocialChannelEnum['TikTok Post']:
        return 'tiktok';
      case SocialChannelEnum['Instagram Reel']:
      case SocialChannelEnum['Instagram Story']:
        return 'instagram';
      case SocialChannelEnum['YouTube Shorts']:
        return 'youtube';
      default:
        return null;
    }
  }

  /** Platform label for the V2 section header (e.g. "TikTok", "Instagram Reel"). */
  getShoutBriefChannelLabel(): string {
    const platform = this.brief()?.shoutBrief?.socialPlatform;
    if (platform === undefined || platform === null) return '';
    switch (Number(platform)) {
      case SocialChannelEnum['TikTok Post']:
        return 'TikTok';
      case SocialChannelEnum['Instagram Reel']:
        return 'Instagram Reel';
      case SocialChannelEnum['Instagram Story']:
        return 'Instagram Story';
      case SocialChannelEnum['YouTube Shorts']:
        return 'YouTube Shorts';
      default:
        return '';
    }
  }

  saveAutoHireConfig() {
    console.log('saveAutoHireConfig: ', this.selectedDemographics());

    let influencerLevelByChannel: InfluencerLevelByChannel | undefined = this.selectedDemographics()?.influencerLevelByChannel;
    let followerRanges: { min: number; max: number }[] = [];
    if (influencerLevelByChannel) {
      Object.keys(influencerLevelByChannel).forEach(channel => {
        const level = influencerLevelByChannel[channel as InfluencerChannel];
        if (level) {
          followerRanges.push({ min: level.followersFrom, max: level.followersTo });
        }
      });
    }

    if (followerRanges.length > 0) {
      this.selectedDemographics.set({ ...this.selectedDemographics()!, followerRanges: followerRanges } as AddBriefDemographicsDto);
    }

    console.log('VALID: ', this.selectedDemographics());

    this.briefService.addBriefDemographics({ ...this.selectedDemographics()!, briefId: this.briefId()! } as CreateBriefDemographicDto).subscribe((response: any) => {
      console.log('Add Demographics Response: ', response);
    }, error => {
      console.error('error adding brief demographics: ', error);
    });
  }

  toggleAutoPilot(event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    const b = this.brief();
    if (b) {
      this.brief.set({ ...b, enableAutoPilot: checked });
    }
  }

  backToBrief() {
    this.location.back();
  }
}
